/**
 * @packageDocumentation
 * @module f1/fn
 */

export interface CurryN {
  /**
   * Curry any function up to five parameters. This allows us to create methods declaratively if the requirements of the function
   * involve more than one parameter
   *
   * @public
   * @function f1.cn
   * @example
   *
   * ```ts
   * 	always(true)() === true
   * ```
   */

  // cn x1
  <P1, R>(pN: 1, fn: (p1: P1) => R): (p1: P1) => R;
  <P1, R>(pN: 1, fn: (p1: P1) => R, p1: P1): () => R;

  // cn x2
  <P1, P2, R>(pN: 2, fn: (p1: P1, p2: P2) => R): (p1: P1, p2: P2) => R;
  <P1, P2, R>(pN: 2, fn: (p1: P1, p2: P2) => R, p1: P1): (p2: P2) => R;
  <P1, P2, R>(pN: 2, fn: (p1: P1, p2: P2) => R, p1: P1, p2: P2): () => R;

  // cn x3
  <P1, P2, P3, R>(pN: 3, fn: (p1: P1, p2: P2, p3: P3) => R): (p1: P1, p2: P2, p3: P3) => R;
  <P1, P2, P3, R>(pN: 3, fn: (p1: P1, p2: P2, p3: P3) => R, p1: P1): (p2: P2, p3: P3) => R;
  <P1, P2, P3, R>(pN: 3, fn: (p1: P1, p2: P2, p3: P3) => R, p1: P1, p2: P2): (p3: P3) => R;
  <P1, P2, P3, R>(pN: 3, fn: (p1: P1, p2: P2, p3: P3) => R, p1: P1, p2: P2, p3: P3): () => R;

  // cn x4
  <P1, P2, P3, P4, R>(pN: 4, fn: (p1: P1, p2: P2, p3: P3, p4: P4) => R): (p1: P1, p2: P2, p3: P3, p4: P4) => R;
  <P1, P2, P3, P4, R>(pN: 4, fn: (p1: P1, p2: P2, p3: P3, p4: P4) => R, p1: P1): (p2: P2, p3: P3, p4: P4) => R;
  <P1, P2, P3, P4, R>(pN: 4, fn: (p1: P1, p2: P2, p3: P3, p4: P4) => R, p1: P1, p2: P2): (p3: P3, p4: P4) => R;
  <P1, P2, P3, P4, R>(pN: 4, fn: (p1: P1, p2: P2, p3: P3, p4: P4) => R, p1: P1, p2: P2, p3: P3): (p4: P4) => R;
  <P1, P2, P3, P4, R>(pN: 4, fn: (p1: P1, p2: P2, p3: P3, p4: P4) => R, p1: P1, p2: P2, p3: P3, p4: P4): () => R;

  // cn x5
  <P1, P2, P3, P4, P5, R>(pN: 5, fn: (p1: P1, p2: P2, p3: P3, p4: P4, p5: P5) => R): (
    p1: P1,
    p2: P2,
    p3: P3,
    p4: P4,
    p5: P5
  ) => R;
  <P1, P2, P3, P4, P5, R>(pN: 5, fn: (p1: P1, p2: P2, p3: P3, p4: P4, p5: P5) => R, p1: P1): (
    p2: P2,
    p3: P3,
    p4: P4,
    p5: P5
  ) => R;
  <P1, P2, P3, P4, P5, R>(pN: 5, fn: (p1: P1, p2: P2, p3: P3, p4: P4, p5: P5) => R, p1: P1, p2: P2): (
    p3: P3,
    p4: P4,
    p5: P5
  ) => R;
  <P1, P2, P3, P4, P5, R>(pN: 5, fn: (p1: P1, p2: P2, p3: P3, p4: P4, p5: P5) => R, p1: P1, p2: P2, p3: P3): (
    p4: P4,
    p5: P5
  ) => R;
  <P1, P2, P3, P4, P5, R>(pN: 5, fn: (p1: P1, p2: P2, p3: P3, p4: P4, p5: P5) => R, p1: P1, p2: P2, p3: P3, p4: P4): (
    p5: P5
  ) => R;
  <P1, P2, P3, P4, P5, R>(
    pN: 5,
    fn: (p1: P1, p2: P2, p3: P3, p4: P4, p5: P5) => R,
    p1: P1,
    p2: P2,
    p3: P3,
    p4: P4,
    p5: P5
  ): () => R;
}

/**
 * @see CurryN
 */
export default (parameterCount: number, fn: Function, ...known: unknown[]) => (...captured: unknown[]) =>
  fn.apply(undefined, [...known, ...captured]);
